import { useState, useEffect, useContext, useCallback, useRef } from "react";
import { useNotification } from "../contexts/NotificationContext";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { apiService } from "../backend/api";
import {
  CheckCircle2,
  Download,
  RefreshCw,
  X,
  ShieldCheck,
  Calendar,
  ArrowRight,
  Info,
  ChevronRight,
  Loader2,
  Package,
  Star,
  Zap,
  Crown,
  AlertCircle,
  Tag,
  XCircle,
  Sparkles
} from "lucide-react";

import { LoginContext } from "../LoginContext";
import {
  initiateSubscription,
  scheduleSubscriptionChange,
  openBillingPortal,
} from "../services/stripeService";
import { getBaseUrl } from "../config/apiConfig";

const Pakete = () => {
  const { showNotification } = useNotification();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("subscriptions");
  const { loginInfo } = useContext(LoginContext);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dbPlans, setDbPlans] = useState([]);
  const [allPlans, setAllPlans] = useState([]);
  const [activePromos, setActivePromos] = useState([]);
  const [launchPromos, setLaunchPromos] = useState(null);

  // Modal states
  const [showChangeModal, setShowChangeModal] = useState(false);
  const [showScheduleChangeModal, setShowScheduleChangeModal] = useState(false);
  const [showCancelScheduledModal, setShowCancelScheduledModal] = useState(false);

  const [selectedPlan, setSelectedPlan] = useState(null);
  const [scheduleChangeData, setScheduleChangeData] = useState(null);
  const [upgradeMode, setUpgradeMode] = useState('immediate'); // 'immediate' oder 'next_period'
  const [billingPortalLoading, setBillingPortalLoading] = useState(false);
  const mobileScrollRef = useRef(null);
  const [mobileCardHeight, setMobileCardHeight] = useState(null);

  const loggedInUser = loginInfo?.username;

  const fetchSubscription = useCallback(async () => {
    if (!loggedInUser) return;
    setLoading(true);
    try {
      const data = await apiService.request(
        `${getBaseUrl()}/api/payment/user-subscriptions/${encodeURIComponent(loggedInUser)}`
      );
      setSubscription(data.subscription);
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error("Error loading subscription data:", error);
      }
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  }, [loggedInUser]);

  const fetchPlans = useCallback(async () => {
    try {
      const data = await apiService.request(`${getBaseUrl()}/api/payment/plans`);
      setAllPlans(data);
      setDbPlans(data.filter(p => p.type === 'subscription'));
    } catch (error) {
      console.error("Error loading plans:", error);
    }
  }, []);

  const fetchPromos = useCallback(async () => {
    try {
      const data = await apiService.request(`${getBaseUrl()}/api/payment/active-promos`);
      setActivePromos(data);
    } catch {
      setActivePromos([]);
    }
  }, []);

  const fetchLaunchPromos = useCallback(async () => {
    try {
      const data = await apiService.request(`${getBaseUrl()}/api/payment/launch-promotions`);
      setLaunchPromos(data);
    } catch {
      setLaunchPromos(null);
    }
  }, []);

  useEffect(() => {
    fetchSubscription();
    fetchPlans();
    fetchPromos();
    fetchLaunchPromos();
  }, [fetchSubscription, fetchPlans, fetchPromos, fetchLaunchPromos]);

  const handleSubscription = (plan) => {
    const price = Number.parseFloat(plan.priceMonthly.replace("€", "").replace(",", "."));
    initiateSubscription(plan.title, price, loggedInUser);
  };



  const handlePlanChange = (plan) => {
    setSelectedPlan(plan);
    if (subscription && subscription.status === 'aktiv') {
      handleScheduledPlanChange(plan);
    } else {
      setShowChangeModal(true);
    }
  };

  const handleScheduledPlanChange = async (plan) => {
    if (!loggedInUser) return;
    // Zeige zuerst nur das Bestätigungs-Modal mit Plan-Daten
    // Backend-Call erfolgt erst NACH Bestätigung
    setSelectedPlan(plan);
    setUpgradeMode('immediate'); // Reset auf default beim Öffnen
    setShowScheduleChangeModal(true);
  };

  const handleCancelScheduledChange = async () => {
    const username = subscription?.username || loggedInUser;
    if (!username) return;
    setLoading(true);
    try {
      await apiService.request(`${getBaseUrl()}/api/payment/cancel-scheduled-change`, {
        method: "POST",
        body: JSON.stringify({ username })
      });
      setShowCancelScheduledModal(false); // Schließe Modal
      await fetchSubscription();
      showNotification(t("pakete.cancelScheduledSuccess"), 'success');
    } catch (error) {
      showNotification(t("pakete.cancelScheduledError") + ': ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const confirmScheduledChange = async () => {
    // Jetzt erst den Backend-Call machen nach Bestätigung
    if (!loggedInUser || !selectedPlan) return;

    setLoading(true);
    try {
      const data = await scheduleSubscriptionChange(selectedPlan.title, loggedInUser, upgradeMode);
      setScheduleChangeData(data);

      // Zeige Erfolgs-Nachricht
      if (data.success) {
        showNotification(data.message || t("pakete.planChangeSuccess"), 'success');
      }

      // Schließe Modal und aktualisiere Subscription
      setShowScheduleChangeModal(false);
      await fetchSubscription();
    } catch (error) {
      showNotification(t("pakete.planChangeError") + ': ' + error.message, 'error');
    } finally {
      setLoading(false);
      setSelectedPlan(null);
      setScheduleChangeData(null);
      setUpgradeMode('immediate'); // Reset auf default
    }
  };

  const confirmChange = async () => {
    if (!selectedPlan) {
      setShowChangeModal(false);
      return;
    }

    const price = Number.parseFloat(selectedPlan.priceMonthly.replace("€", "").replace(",", "."));

    // Free-Plan: Direkt über Backend erstellen, KEIN Stripe-Checkout
    if (selectedPlan.title === 'Free' || price === 0) {
      setLoading(true);
      try {
        const data = await scheduleSubscriptionChange('Free', loggedInUser, 'immediate');
        if (data.success) {
          showNotification(data.message || t("pakete.planChangeSuccess"), 'success');
        }
        await fetchSubscription();
      } catch (error) {
        showNotification(t("pakete.planChangeError") + ': ' + error.message, 'error');
      } finally {
        setLoading(false);
      }
    } else {
      handleSubscription(selectedPlan);
    }

    setShowChangeModal(false);
    setSelectedPlan(null);
  };

  const iconMap = {
    Package,
    Zap,
    Star,
    Crown,
    Download
  };

  // Einzelpreis aus One-Time-Paketen ableiten (höchster Pro-Download-Preis = Einzelkauf)
  const oneTimePlans = allPlans.filter(p => p.type === 'one-time' && p.downloads > 0);
  const freePerDownload = oneTimePlans.length > 0
    ? Math.max(...oneTimePlans.map(p => p.price / p.downloads))
    : 0; // Kein Fallback – Preise kommen aus der DB

  const subDiscountPercent = launchPromos?.subscriptionDiscount?.percent || 0;

  const pricingPlans = dbPlans.map(plan => {
    const pricePerDl = plan.downloads > 0 ? plan.price / plan.downloads : 0;
    const savingsPercent = pricePerDl > 0 && pricePerDl < freePerDownload
      ? Math.round((1 - pricePerDl / freePerDownload) * 100)
      : 0;
    const launchPrice = subDiscountPercent > 0 && plan.price > 0
      ? plan.price * (1 - subDiscountPercent / 100)
      : null;
    return {
      title: plan.name,
      id: plan.name.toLowerCase(),
      priceMonthly: `${plan.price.toFixed(2).replace('.', ',')}€`,
      priceRaw: plan.price,
      launchPriceMonthly: launchPrice ? `${launchPrice.toFixed(2).replace('.', ',')}€` : null,
      launchDurationMonths: launchPromos?.subscriptionDiscount?.durationMonths || 0,
      downloads: plan.downloads.toString(),
      downloadsRaw: plan.downloads,
      pricePerDownload: pricePerDl > 0 ? `${pricePerDl.toFixed(2).replace('.', ',')}€` : `${freePerDownload.toFixed(2).replace('.', ',')}€`,
      savingsPercent,
      missingFeatures: plan.missingFeatures || [],
      highlightFeatures: plan.highlightFeatures || [],
      priceLabel: pricePerDl > 0
        ? `${pricePerDl.toFixed(2).replace('.', ',')}€ / Download`
        : (freePerDownload > 0 ? `${freePerDownload.toFixed(2).replace('.', ',')}€ / Download` : ''),
      savingsLabel: savingsPercent > 0 ? `${savingsPercent}% Rabatt` : '',
      icon: iconMap[plan.icon] || Package,
      description: plan.features || [],
      isRecommended: plan.name === 'Standard',
      headerBg: plan.name === 'Free' ? "bg-slate-800" :
                plan.name === 'Starter' ? "bg-[#1e3a47]" :
                plan.name === 'Basic' ? "bg-[#1e4a5a]" :
                plan.name === 'Standard' ? "bg-[#0f7699]" :
                plan.name === 'Professional' ? "bg-[#0d6b8a]" : "bg-[#5c8a9d]"
    };
  });

  // Auto-scroll mobile carousel to active/planned subscription (horizontal only)
  useEffect(() => {
    if (!mobileScrollRef.current || pricingPlans.length === 0) return;
    const targetIndex = pricingPlans.findIndex(plan => {
      if (subscription?.status === "geplant" && subscription?.planName === plan.title) return true;
      if (subscription?.scheduledChange?.newPlanName === plan.title) return true;
      return false;
    });
    const activeIndex = targetIndex >= 0 ? targetIndex : pricingPlans.findIndex(plan =>
      subscription?.planName === plan.title && (subscription?.status === "aktiv" || subscription?.status === "wechselnd")
    );
    if (activeIndex > 0) {
      const container = mobileScrollRef.current;
      const card = container.children[activeIndex];
      if (card) {
        const savedScrollY = window.scrollY;
        setTimeout(() => {
          container.scrollLeft = card.offsetLeft - container.offsetLeft - (container.clientWidth - card.clientWidth) / 2;
          window.scrollTo(0, savedScrollY);
        }, 150);
      }
    }
  }, [pricingPlans, subscription]);

  // Dynamisch verfügbare Höhe für Mobile-Karten berechnen
  useEffect(() => {
    const calcHeight = () => {
      if (!mobileScrollRef.current) return;
      const rect = mobileScrollRef.current.getBoundingClientRect();
      const available = window.innerHeight - rect.top - 32;
      setMobileCardHeight(Math.max(available, 280));
    };
    calcHeight();
    window.addEventListener('resize', calcHeight);
    const timer = setTimeout(calcHeight, 250);
    return () => { window.removeEventListener('resize', calcHeight); clearTimeout(timer); };
  }, [pricingPlans, subscription]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 relative font-sans transition-colors duration-300">
      {/* Page Header - Full Width */}
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 shrink-0 relative z-10">
        <div className="px-3 sm:px-6 lg:px-8 py-2 sm:py-5">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-14 sm:h-14 bg-[#0f7699]/10 dark:bg-[#4fc3f7]/15 rounded-xl sm:rounded-2xl flex items-center justify-center shrink-0">
              <Tag className="text-[#0f7699] dark:text-[#4fc3f7]" size={22} />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg sm:text-2xl md:text-3xl font-black text-[#0f7699] dark:text-[#4fc3f7] tracking-tight">
                {t("Preise")}
              </h1>
              <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 font-medium mt-0.5 hidden sm:block">
                {t("paketepage.subtitle")}
              </p>
            </div>
          </div>
          {/* Mobile Tab Navigation inside Header */}
          <div className="flex lg:hidden mt-2 bg-slate-100/80 dark:bg-slate-800/80 p-1 rounded-xl">
            <button
              onClick={() => { setActiveTab("subscriptions"); navigate("/pakete"); }}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg font-bold text-sm transition-all duration-200 ${activeTab === "subscriptions" ? 'bg-white dark:bg-slate-700 text-[#0f7699] dark:text-[#4fc3f7] shadow-sm' : 'text-slate-400 dark:text-slate-500'}`}
            >
              <ShieldCheck size={15} />
              <span>{t("pakete.subscriptions")}</span>
            </button>
            <button
              onClick={() => { setActiveTab("downloads"); navigate("/downloadspakete"); }}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg font-bold text-sm transition-all duration-200 ${activeTab === "downloads" ? 'bg-white dark:bg-slate-700 text-[#0f7699] dark:text-[#4fc3f7] shadow-sm' : 'text-slate-400 dark:text-slate-500'}`}
            >
              <Download size={15} />
              <span>{t("pakete.downloads")}</span>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Compact Status Banners — directly below header */}
      <div className="lg:hidden relative z-10 space-y-0">
        {subscription?.status === "geplant" && (
          <div className="bg-sky-50 dark:bg-sky-950/40 border-b border-sky-100 dark:border-sky-900/50 px-3 py-2.5 flex items-center gap-2.5">
            <Calendar size={15} className="text-[#0f7699] dark:text-[#4fc3f7] shrink-0" />
            <p className="text-xs text-slate-600 dark:text-slate-300 leading-snug flex-1 min-w-0">
              <strong>{subscription.planName}</strong> {t("pakete.subscriptionStartsOn")} <strong>{new Date(subscription.scheduledActivation?.activationDate).toLocaleDateString('de-DE')}</strong>
            </p>
          </div>
        )}
        {subscription?.scheduledChange && subscription.scheduledChange.upgradeMode !== 'immediate' && (
          <div className="bg-orange-50 dark:bg-orange-950/40 border-b border-orange-100 dark:border-orange-900/50 px-3 py-2.5 flex items-center gap-2.5">
            <RefreshCw size={14} className="text-orange-500 dark:text-orange-400 animate-spin-slow shrink-0" />
            <p className="text-xs text-slate-600 dark:text-slate-300 leading-snug flex-1 min-w-0">
              {t("pakete.switchTo")} <strong>{subscription.scheduledChange?.newPlanName}</strong> {t("pakete.on")} <strong>{new Date(subscription.scheduledChange?.effectiveDate).toLocaleDateString('de-DE')}</strong>
            </p>
            <button
              onClick={() => setShowCancelScheduledModal(true)}
              className="text-[10px] font-bold text-orange-600 dark:text-orange-400 bg-white dark:bg-slate-800 border border-orange-200 dark:border-orange-800 px-2.5 py-1 rounded-lg shrink-0 active:scale-95"
            >
              {t("pakete.cancel")}
            </button>
          </div>
        )}
        {(subscription?.status === 'payment_failed' || subscription?.paymentFailedCount > 0) && (
          <div className="bg-red-50 dark:bg-red-950/40 border-b border-red-100 dark:border-red-900/50 px-3 py-2.5 flex items-center gap-2.5">
            <AlertCircle size={15} className="text-red-500 dark:text-red-400 shrink-0" />
            <p className="text-xs text-slate-600 dark:text-slate-300 leading-snug flex-1 min-w-0">
              {subscription?.status === 'payment_failed'
                ? <><strong>{t("pakete.subscriptionBlocked")}</strong> — {t("pakete.paymentFailedCount", { count: subscription.paymentFailedCount })}</>
                : <>{t("pakete.paymentFailed")} ({subscription.paymentFailedCount}/3) — {t("pakete.checkPaymentMethod")}</>
              }
            </p>
          </div>
        )}
        {activePromos.length > 0 && (
          <div className="bg-green-50 dark:bg-green-950/40 border-b border-green-100 dark:border-green-900/50 px-3 py-2.5 flex items-center gap-2.5">
            <Tag size={15} className="text-green-600 dark:text-green-400 shrink-0" />
            <p className="text-xs text-slate-600 dark:text-slate-300 leading-snug flex-1 min-w-0">
              {activePromos.map((promo, i) => (
                <span key={i}>
                  {i > 0 && ' · '}
                  <strong>{promo.code}</strong>{' '}
                  {promo.percent_off ? `−${promo.percent_off}%` : promo.amount_off ? `−${promo.amount_off}€` : ''}
                </span>
              ))}
            </p>
          </div>
        )}
        {launchPromos?.subscriptionDiscount && (
          <div className="bg-purple-50 dark:bg-purple-950/40 border-b border-purple-100 dark:border-purple-900/50 px-3 py-2.5 flex items-center gap-2.5">
            <Sparkles size={15} className="text-purple-600 dark:text-purple-400 shrink-0" />
            <p className="text-xs text-slate-600 dark:text-slate-300 leading-snug flex-1 min-w-0">
              <strong>Launch-Aktion:</strong> {launchPromos.subscriptionDiscount.percent}% Rabatt auf Abos — erste {launchPromos.subscriptionDiscount.durationMonths} Monate. Wird automatisch angewendet!
            </p>
          </div>
        )}
        {launchPromos?.registrationBonus && (
          <div className="bg-amber-50 dark:bg-amber-950/40 border-b border-amber-100 dark:border-amber-900/50 px-3 py-2.5 flex items-center gap-2.5">
            <Star size={15} className="text-amber-600 dark:text-amber-400 shrink-0" />
            <p className="text-xs text-slate-600 dark:text-slate-300 leading-snug flex-1 min-w-0">
              <strong>Bonus:</strong> {launchPromos.registrationBonus.downloads} kostenlose Downloads bei Registrierung!
            </p>
          </div>
        )}
      </div>

      {/* Background Decor */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-gradient-to-b from-[#0f7699]/10 dark:from-[#4fc3f7]/10 to-transparent rounded-full blur-[120px] -z-10" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 lg:py-8 md:py-12 relative z-10">

        {/* Navigation Tabs — Desktop only */}
        <div className="hidden lg:flex justify-center mb-8 md:mb-16">
          <div className="bg-white/50 dark:bg-slate-800/50 backdrop-blur-xl p-1.5 rounded-2xl border border-white dark:border-slate-700 flex shadow-xl shadow-slate-200/50 dark:shadow-black/20">
            <button
              onClick={() => { setActiveTab("subscriptions"); navigate("/pakete"); }}
              className={`flex items-center space-x-2 px-3 sm:px-6 py-2.5 sm:py-3 rounded-xl font-bold transition-all duration-300 ${activeTab === "subscriptions" ? 'bg-[#0f7699] dark:bg-[#4fc3f7] text-white dark:text-slate-900 shadow-lg shadow-sky-900/20' : 'text-slate-500 dark:text-slate-400 hover:text-[#0f7699] dark:hover:text-[#4fc3f7] hover:bg-[#0f7699]/5 dark:hover:bg-[#4fc3f7]/10'}`}
            >
              <ShieldCheck size={18} />
              <span className="hidden sm:inline">{t("pakete.subscriptionsLong")}</span>
              <span className="sm:hidden text-sm">{t("pakete.subscriptions")}</span>
            </button>
            <button
              onClick={() => { setActiveTab("downloads"); navigate("/downloadspakete"); }}
              className={`flex items-center space-x-2 px-3 sm:px-6 py-2.5 sm:py-3 rounded-xl font-bold transition-all duration-300 ${activeTab === "downloads" ? 'bg-[#0f7699] dark:bg-[#4fc3f7] text-white dark:text-slate-900 shadow-lg' : 'text-slate-500 dark:text-slate-400 hover:text-[#0f7699] dark:hover:text-[#4fc3f7] hover:bg-[#0f7699]/5 dark:hover:bg-[#4fc3f7]/10'}`}
            >
              <Download size={18} />
              <span className="hidden sm:inline">{t("pakete.downloadPackages")}</span>
              <span className="sm:hidden text-sm">{t("pakete.downloads")}</span>
            </button>
          </div>
        </div>

        {/* Status Banners — Desktop only */}
        <div className="hidden lg:block max-w-7xl mx-auto mb-8 md:mb-12 space-y-4">
          {subscription?.status === "geplant" && (
             <div className="bg-sky-50 border border-sky-100 rounded-2xl sm:rounded-3xl p-4 sm:p-6 flex items-start space-x-3 sm:space-x-5 animate-in slide-in-from-top-4 duration-500">
               <div className="bg-white p-2.5 sm:p-3 rounded-xl sm:rounded-2xl shadow-sm text-[#0f7699]">
                 <Calendar size={20} className="sm:hidden" />
                 <Calendar size={24} className="hidden sm:block" />
               </div>
               <div className="flex-1 space-y-1 min-w-0">
                 <h3 className="font-bold text-slate-800 text-sm sm:text-base">{t("pakete.subscriptionPlanned")}</h3>
                 <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                   {t("pakete.yourNew")} <strong>{subscription.planName}</strong> {t("pakete.subscriptionStartsOn")} <strong>{new Date(subscription.scheduledActivation?.activationDate).toLocaleDateString('de-DE')}</strong>.
                 </p>
               </div>
             </div>
          )}

          {/* Zeige "Geplanter Abo-Wechsel" nur wenn scheduledChange existiert (nicht bei immediate upgrades) */}
          {subscription?.scheduledChange && subscription.scheduledChange.upgradeMode !== 'immediate' && (
             <div className="bg-orange-50 border border-orange-100 rounded-2xl sm:rounded-3xl p-4 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 animate-in slide-in-from-top-4 duration-500">
               <div className="flex items-start space-x-3 sm:space-x-5">
                 <div className="bg-white p-2.5 sm:p-3 rounded-xl sm:rounded-2xl shadow-sm text-orange-600">
                   <RefreshCw size={20} className="animate-spin-slow sm:hidden" />
                   <RefreshCw size={24} className="animate-spin-slow hidden sm:block" />
                 </div>
                 <div className="space-y-1 min-w-0">
                   <h3 className="font-bold text-slate-800 text-sm sm:text-base">{t("pakete.scheduledPlanChange")}</h3>
                   <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                     {t("pakete.switchFrom")} <strong>{subscription.planName}</strong> {t("pakete.to")} <strong>{subscription.scheduledChange?.newPlanName}</strong> {t("pakete.on")} <strong>{new Date(subscription.scheduledChange?.effectiveDate).toLocaleDateString('de-DE')}</strong>.
                   </p>
                 </div>
               </div>
               <button
                 onClick={() => setShowCancelScheduledModal(true)}
                 className="self-end sm:self-auto px-4 sm:px-5 py-2 sm:py-2.5 bg-white text-orange-600 hover:bg-orange-600 hover:text-white border border-orange-100 rounded-xl font-bold text-xs sm:text-sm transition-all shadow-sm active:scale-95 shrink-0"
               >
                 {t("pakete.cancel")}
               </button>
             </div>
          )}

          {(subscription?.status === 'payment_failed' || subscription?.paymentFailedCount > 0) && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 rounded-2xl sm:rounded-3xl p-4 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 animate-in slide-in-from-top-4 duration-500">
              <div className="flex items-start space-x-3 sm:space-x-5">
                <div className="bg-white dark:bg-slate-800 p-2.5 sm:p-3 rounded-xl sm:rounded-2xl shadow-sm text-red-600 dark:text-red-400">
                  <AlertCircle size={24} />
                </div>
                <div className="space-y-1 min-w-0">
                  <h3 className="font-bold text-slate-800 dark:text-white text-sm sm:text-base">
                    {subscription?.status === 'payment_failed' ? t("pakete.subscriptionBlocked") : t("pakete.paymentFailed")}
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                    {subscription?.status === 'payment_failed'
                      ? <>{t("pakete.subscriptionBlockedAfter", { count: subscription.paymentFailedCount })}</>
                      : <>{t("pakete.paymentAttemptFailed", { count: subscription?.paymentFailedCount })}</>
                    }
                  </p>
                </div>
              </div>
              <button
                onClick={async () => {
                  if (billingPortalLoading) return;
                  setBillingPortalLoading(true);
                  try {
                    await openBillingPortal(loggedInUser);
                  } catch (error) {
                    showNotification(t("pakete.paymentPortalError") + ': ' + error.message, 'error');
                    setBillingPortalLoading(false);
                  }
                }}
                disabled={billingPortalLoading}
                className="self-end sm:self-auto px-4 sm:px-5 py-2 sm:py-2.5 bg-red-600 text-white hover:bg-red-700 border border-red-600 rounded-xl font-bold text-xs sm:text-sm transition-all shadow-sm active:scale-95 shrink-0 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {billingPortalLoading ? t("pakete.loading") : t("pakete.updatePaymentMethod")}
              </button>
            </div>
          )}

          {activePromos.length > 0 && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800/30 rounded-2xl sm:rounded-3xl p-4 sm:p-6 flex items-start space-x-3 sm:space-x-5 animate-in slide-in-from-top-4 duration-500">
              <div className="bg-white dark:bg-slate-800 p-2.5 sm:p-3 rounded-xl sm:rounded-2xl shadow-sm text-green-600 dark:text-green-400">
                <Tag size={24} />
              </div>
              <div className="flex-1 space-y-1 min-w-0">
                <h3 className="font-bold text-slate-800 dark:text-white text-sm sm:text-base">{t("pakete.activeDiscountCodes")}</h3>
                <div className="flex flex-wrap gap-2">
                  {activePromos.map((promo, i) => (
                    <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1 bg-white dark:bg-slate-800 border border-green-200 dark:border-green-800/30 rounded-full text-xs font-bold text-green-700 dark:text-green-400">
                      <Tag size={12} />
                      {promo.code} — {promo.percent_off ? `${promo.percent_off}%` : promo.amount_off ? `${promo.amount_off}€` : ''} {t("pakete.discount")}
                    </span>
                  ))}
                </div>
                <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-1">{t("pakete.enterCodeAtCheckout")}</p>
              </div>
            </div>
          )}

          {/* Launch Promotion Banners — Desktop */}
          {launchPromos?.subscriptionDiscount && (
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border border-purple-100 dark:border-purple-800/30 rounded-2xl sm:rounded-3xl p-4 sm:p-6 flex items-start space-x-3 sm:space-x-5 animate-in slide-in-from-top-4 duration-500">
              <div className="bg-white dark:bg-slate-800 p-2.5 sm:p-3 rounded-xl sm:rounded-2xl shadow-sm text-purple-600 dark:text-purple-400">
                <Sparkles size={24} />
              </div>
              <div className="flex-1 space-y-1 min-w-0">
                <h3 className="font-bold text-slate-800 dark:text-white text-sm sm:text-base">Launch-Aktion: {launchPromos.subscriptionDiscount.percent}% Abo-Rabatt</h3>
                <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                  Spare {launchPromos.subscriptionDiscount.percent}% auf jedes Abonnement — für die ersten {launchPromos.subscriptionDiscount.durationMonths} Monate. Wird automatisch beim Checkout angewendet!
                </p>
                {launchPromos.subscriptionDiscount.endDate && (
                  <p className="text-[10px] text-slate-500 dark:text-slate-400">Gültig bis {new Date(launchPromos.subscriptionDiscount.endDate).toLocaleDateString('de-DE')}</p>
                )}
              </div>
            </div>
          )}
          {launchPromos?.registrationBonus && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800/30 rounded-2xl sm:rounded-3xl p-4 sm:p-6 flex items-start space-x-3 sm:space-x-5 animate-in slide-in-from-top-4 duration-500">
              <div className="bg-white dark:bg-slate-800 p-2.5 sm:p-3 rounded-xl sm:rounded-2xl shadow-sm text-amber-600 dark:text-amber-400">
                <Star size={24} />
              </div>
              <div className="flex-1 space-y-1 min-w-0">
                <h3 className="font-bold text-slate-800 dark:text-white text-sm sm:text-base">Registrierungs-Bonus: {launchPromos.registrationBonus.downloads} kostenlose Downloads</h3>
                <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                  Jeder neue Nutzer erhält {launchPromos.registrationBonus.downloads} Bonus-Downloads bei Registrierung — sofort einsatzbereit!
                </p>
                {launchPromos.registrationBonus.endDate && (
                  <p className="text-[10px] text-slate-500 dark:text-slate-400">Gültig bis {new Date(launchPromos.registrationBonus.endDate).toLocaleDateString('de-DE')}</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Pricing Grid — Desktop */}
        <div className="hidden lg:flex items-stretch justify-center gap-0 bg-white dark:bg-slate-800 rounded-[2rem] overflow-hidden shadow-2xl border border-slate-100 dark:border-slate-700 max-w-7xl mx-auto">
          {pricingPlans.map((plan, index) => {
            const isActive = subscription?.planName === plan.title && (subscription?.status === "aktiv" || subscription?.status === "wechselnd");
            const isGeplant = subscription?.planName === plan.title && subscription?.status === "geplant";
            const isNextMonth = subscription?.scheduledChange?.newPlanName === plan.title;

            return (
              <div
                key={plan.title}
                className={`flex-1 flex flex-col border-r border-slate-100 dark:border-slate-700 last:border-r-0 transition-all duration-300 hover:z-30 hover:shadow-[0_0_50px_rgba(0,0,0,0.1)] dark:hover:shadow-[0_0_50px_rgba(0,0,0,0.3)] relative ${plan.isRecommended ? 'bg-slate-50/30 dark:bg-slate-700/30' : 'bg-white dark:bg-slate-800'}`}
              >
                <div className={`${plan.headerBg} p-6 text-center text-white relative`}>
                  <h3 className="text-xs font-black uppercase tracking-[0.15em] mb-4 opacity-90">{plan.title}</h3>
                  <div className="space-y-1 mb-2">
                    {plan.launchPriceMonthly ? (
                      <>
                        <span className="text-lg font-light tracking-tight line-through opacity-50">{plan.priceMonthly.replace(',00', '')}</span>
                        <br />
                        <span className="text-3xl xl:text-4xl font-extralight tracking-tight">{plan.launchPriceMonthly.replace(',00', '')}</span>
                        <div className="text-[9px] font-black uppercase tracking-widest opacity-60">erste {plan.launchDurationMonths} Monate</div>
                        <div className="text-[9px] font-black uppercase tracking-widest opacity-80 text-green-300">-{subDiscountPercent}% Launch-Rabatt</div>
                      </>
                    ) : (
                      <>
                        <span className="text-3xl xl:text-4xl font-extralight tracking-tight">{plan.priceMonthly.replace(',00', '')}</span>
                        <div className="text-[9px] font-black uppercase tracking-widest opacity-60">{t("paketepage.perMonth")}</div>
                      </>
                    )}
                  </div>
                  <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[12px] border-l-transparent border-r-[12px] border-r-transparent border-t-[12px]" style={{ borderTopColor: plan.title === 'Starter' ? '#1e3a47' : plan.title === 'Basic' ? '#1e4a5a' : plan.title === 'Standard' ? '#0f7699' : plan.title === 'Professional' ? '#0d6b8a' : plan.title === 'Enterprise' ? '#5c8a9d' : '#1e293b' }}></div>
                </div>

                <div className="flex-1 flex flex-col p-5 bg-white dark:bg-slate-800">
                  <div className="flex flex-col items-center space-y-4 mb-8">
                    <div className="text-center">
                      <div className="text-lg font-black text-slate-800 dark:text-white">{plan.downloads}</div>
                      <div className="text-[9px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">{t("paketepage.downloadsPerMonth")}</div>
                      {plan.priceLabel && (
                        <div className="text-xs font-semibold text-[#0f7699] dark:text-[#4fc3f7] mt-2">{plan.priceLabel}</div>
                      )}
                    </div>
                    {plan.savingsLabel && (
                      <div className="flex items-center justify-center">
                        <span className="text-[10px] font-black text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-2.5 py-0.5 rounded-full border border-green-100 dark:border-green-800/30">{plan.savingsLabel}</span>
                      </div>
                    )}
                    <div className="w-full h-px bg-slate-50 dark:bg-slate-700" />
                    <div className="w-full space-y-3">
                      {plan.highlightFeatures.map((item, i) => (
                        <div key={`highlight-${i}`} className="flex flex-col items-center justify-center py-3 border-b border-amber-100 dark:border-amber-800/30 last:border-0 group bg-amber-50/50 dark:bg-amber-900/10 rounded-xl">
                          <Sparkles size={16} className="text-amber-500 dark:text-amber-400 mb-1.5 group-hover:scale-110 transition-transform" />
                          <span className="text-[11px] font-bold text-amber-700 dark:text-amber-400 text-center leading-snug">{item}</span>
                        </div>
                      ))}
                      {plan.description.map((item, i) => (
                        <div key={i} className="flex flex-col items-center justify-center py-3 border-b border-slate-50 dark:border-slate-700 last:border-0 group">
                          <CheckCircle2 size={16} className="text-green-500 dark:text-green-400 mb-1.5 group-hover:scale-110 transition-transform" />
                          <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400 text-center leading-snug">{item}</span>
                        </div>
                      ))}
                      {plan.missingFeatures.map((item, i) => (
                        <div key={`missing-${i}`} className="flex flex-col items-center justify-center py-3 border-b border-slate-50 dark:border-slate-700 last:border-0 group">
                          <XCircle size={16} className="text-red-300 dark:text-red-800 mb-1.5" />
                          <span className="text-[11px] font-medium text-slate-300 dark:text-slate-600 text-center leading-snug line-through">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="mt-auto pt-6">
                    <button
                      disabled={isActive || isNextMonth}
                      onClick={() => handlePlanChange(plan)}
                      className={`w-full py-3.5 rounded-xl font-black text-[10px] uppercase tracking-[0.15em] transition-all active:scale-[0.98] ${
                        isActive || isNextMonth
                          ? 'bg-slate-100 dark:bg-slate-700 text-slate-400 dark:text-slate-500 border border-slate-200 dark:border-slate-600 cursor-default'
                          : plan.isRecommended
                            ? 'bg-[#0f7699] dark:bg-[#4fc3f7] text-white dark:text-slate-900 shadow-lg shadow-sky-900/20 hover:bg-[#0d6b8a] dark:hover:bg-[#81d4fa]'
                            : 'bg-slate-800 dark:bg-slate-600 text-white hover:bg-slate-900 dark:hover:bg-slate-500'
                      }`}
                    >
                      {isActive ? t("paketepage.currentlyActive") : isNextMonth ? t("paketepage.scheduledChange") : isGeplant ? t("pakete.ready") : t("paketepage.select")}
                    </button>
                    <div className="flex justify-center mt-3 gap-2">
                      {isActive && <span className="text-[9px] font-black text-green-600 dark:text-green-400 uppercase">{t("paketepage.currentlyActive")}</span>}
                      {isNextMonth && <span className="text-[9px] font-black text-blue-600 dark:text-blue-400 uppercase">{t("paketepage.scheduledChange")}</span>}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Pricing Cards — Mobile Swipe */}
        {(() => {
          const compact = mobileCardHeight && mobileCardHeight < 480;
          return (
            <div className="lg:hidden">
              <div ref={mobileScrollRef} className="flex overflow-x-auto snap-x snap-mandatory gap-4 pb-2 -mx-4 px-4 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}>
                {pricingPlans.map((plan, index) => {
                  const isActive = subscription?.planName === plan.title && (subscription?.status === "aktiv" || subscription?.status === "wechselnd");
                  const isGeplant = subscription?.planName === plan.title && subscription?.status === "geplant";
                  const isNextMonth = subscription?.scheduledChange?.newPlanName === plan.title;

                  return (
                    <div
                      key={plan.title}
                      className={`w-[80vw] max-w-[320px] shrink-0 snap-center flex flex-col rounded-2xl overflow-hidden shadow-xl border border-slate-100 dark:border-slate-700 ${plan.isRecommended ? 'bg-slate-50/30 dark:bg-slate-700/30' : 'bg-white dark:bg-slate-800'}`}
                      style={mobileCardHeight ? { height: mobileCardHeight } : {}}
                    >
                      <div className={`${plan.headerBg} ${compact ? 'p-3' : 'p-5'} text-center text-white relative shrink-0`}>
                        <h3 className={`${compact ? 'text-[10px] mb-1' : 'text-xs mb-3'} font-black uppercase tracking-[0.2em] opacity-90`}>{plan.title}</h3>
                        <div className={compact ? '' : 'space-y-1'}>
                          {plan.launchPriceMonthly ? (
                            <>
                              <span className={`${compact ? 'text-sm' : 'text-base'} font-light tracking-tight line-through opacity-50`}>{plan.priceMonthly.replace(',00', '')}</span>
                              <br />
                              <span className={`${compact ? 'text-2xl' : 'text-3xl'} font-extralight tracking-tight`}>{plan.launchPriceMonthly.replace(',00', '')}</span>
                              <div className={`${compact ? 'text-[8px]' : 'text-[9px]'} font-black uppercase tracking-widest opacity-60`}>erste {plan.launchDurationMonths} Mon.</div>
                              <div className={`${compact ? 'text-[8px]' : 'text-[9px]'} font-black uppercase tracking-widest opacity-80 text-green-300`}>-{subDiscountPercent}% Launch-Rabatt</div>
                            </>
                          ) : (
                            <>
                              <span className={`${compact ? 'text-2xl' : 'text-3xl'} font-extralight tracking-tight`}>{plan.priceMonthly.replace(',00', '')}</span>
                              <div className={`${compact ? 'text-[8px]' : 'text-[9px]'} font-black uppercase tracking-widest opacity-60`}>{t("paketepage.perMonth")}</div>
                            </>
                          )}
                        </div>
                        <div className={`absolute ${compact ? '-bottom-2' : '-bottom-3'} left-1/2 -translate-x-1/2 w-0 h-0 ${compact ? 'border-l-[7px] border-r-[7px] border-t-[7px]' : 'border-l-[10px] border-r-[10px] border-t-[10px]'} border-l-transparent border-r-transparent`} style={{ borderTopColor: index === 1 ? '#1e3a47' : index === 2 ? '#0f7699' : index === 3 ? '#5c8a9d' : '#1e293b' }}></div>
                      </div>

                      <div className={`flex-1 flex flex-col ${compact ? 'p-3' : 'p-4'} bg-white dark:bg-slate-800 overflow-y-auto min-h-0`}>
                        <div className={`flex flex-col items-center ${compact ? 'space-y-2 mb-3' : 'space-y-3 mb-5'}`}>
                          <div className="text-center">
                            <div className={`${compact ? 'text-base' : 'text-lg'} font-black text-slate-800 dark:text-white`}>{plan.downloads}</div>
                            <div className={`${compact ? 'text-[8px]' : 'text-[9px]'} font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest`}>{t("paketepage.downloadsPerMonth")}</div>
                            {!compact && plan.priceLabel && (
                              <div className="text-[11px] font-semibold text-[#0f7699] dark:text-[#4fc3f7] mt-1">{plan.priceLabel}</div>
                            )}
                          </div>
                          {!compact && plan.savingsLabel && (
                            <div className="flex items-center justify-center">
                              <span className="text-[10px] font-black text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-2.5 py-0.5 rounded-full border border-green-100 dark:border-green-800/30">{plan.savingsLabel}</span>
                            </div>
                          )}
                          <div className="w-full h-px bg-slate-100 dark:bg-slate-700" />
                          <div className={`w-full ${compact ? 'space-y-0.5' : 'space-y-1'}`}>
                            {plan.highlightFeatures.map((item, i) => (
                              <div key={`highlight-${i}`} className={`flex items-center gap-2 ${compact ? 'py-1.5' : 'py-2'} border-b border-amber-100 dark:border-amber-800/30 last:border-0 bg-amber-50/50 dark:bg-amber-900/10 rounded-lg px-2`}>
                                <Sparkles size={compact ? 14 : 16} className="text-amber-500 dark:text-amber-400 shrink-0" />
                                <span className={`${compact ? 'text-[10px]' : 'text-[11px]'} font-bold text-amber-700 dark:text-amber-400 leading-snug`}>{item}</span>
                              </div>
                            ))}
                            {plan.description.map((item, i) => (
                              <div key={i} className={`flex items-center gap-2 ${compact ? 'py-1.5' : 'py-2'} border-b border-slate-50 dark:border-slate-700 last:border-0`}>
                                <CheckCircle2 size={compact ? 14 : 16} className="text-green-500 dark:text-green-400 shrink-0" />
                                <span className={`${compact ? 'text-[10px]' : 'text-[11px]'} font-medium text-slate-500 dark:text-slate-400 leading-snug`}>{item}</span>
                              </div>
                            ))}
                            {plan.missingFeatures.map((item, i) => (
                              <div key={`missing-${i}`} className={`flex items-center gap-2 ${compact ? 'py-1.5' : 'py-2'} border-b border-slate-50 dark:border-slate-700 last:border-0`}>
                                <XCircle size={compact ? 14 : 16} className="text-red-300 dark:text-red-800 shrink-0" />
                                <span className={`${compact ? 'text-[10px]' : 'text-[11px]'} font-medium text-slate-300 dark:text-slate-600 leading-snug line-through`}>{item}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className={`mt-auto ${compact ? 'pt-2' : 'pt-4'}`}>
                          <button
                            disabled={isActive || isNextMonth}
                            onClick={() => handlePlanChange(plan)}
                            className={`w-full ${compact ? 'py-3 text-[10px]' : 'py-4 text-xs'} rounded-xl font-black uppercase tracking-[0.15em] transition-all active:scale-[0.98] ${
                              isActive || isNextMonth
                                ? 'bg-slate-100 dark:bg-slate-700 text-slate-400 dark:text-slate-500 border border-slate-200 dark:border-slate-600 cursor-default'
                                : plan.isRecommended
                                  ? 'bg-[#0f7699] dark:bg-[#4fc3f7] text-white dark:text-slate-900 shadow-lg shadow-sky-900/20'
                                  : 'bg-slate-800 dark:bg-slate-600 text-white'
                            }`}
                          >
                            {isActive ? t("paketepage.currentlyActive") : isNextMonth ? t("paketepage.scheduledChange") : isGeplant ? t("pakete.ready") : t("paketepage.select")}
                          </button>
                          {(isActive || isNextMonth) && (
                            <div className={`flex justify-center ${compact ? 'mt-1.5' : 'mt-2'} gap-2`}>
                              {isActive && <span className={`${compact ? 'text-[8px]' : 'text-[9px]'} font-black text-green-600 dark:text-green-400 uppercase`}>{t("paketepage.currentlyActive")}</span>}
                              {isNextMonth && <span className={`${compact ? 'text-[8px]' : 'text-[9px]'} font-black text-blue-600 dark:text-blue-400 uppercase`}>{t("paketepage.scheduledChange")}</span>}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Scroll Indicator Dots */}
              <div className="flex justify-center gap-1.5 py-2 shrink-0">
                {pricingPlans.map((_, index) => (
                  <div key={index} className="w-1.5 h-1.5 rounded-full bg-slate-300 dark:bg-slate-600" />
                ))}
              </div>
            </div>
          );
        })()}

        {/* Global Action Footer — Desktop only */}
        <div className="hidden lg:flex mt-10 sm:mt-20 flex-col md:flex-row items-center justify-center gap-4 sm:gap-6">
          <button
            onClick={() => navigate("/upload")}
            className="flex items-center space-x-3 px-8 py-4 bg-white border-2 border-slate-100 rounded-2xl font-black text-[#0f7699] hover:bg-slate-50 hover:border-[#0f7699]/20 transition-all shadow-xl shadow-slate-200/50"
          >
            <span>{t("buttons.backToUpload")}</span>
            <ChevronRight size={20} />
          </button>
        </div>
      </div>


      {showChangeModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md" onClick={() => setShowChangeModal(false)} />
          <div className="bg-white max-w-md w-full rounded-[1.5rem] sm:rounded-[2.5rem] shadow-2xl relative z-10 overflow-hidden animate-in zoom-in duration-300">
            <div style={{ backgroundColor: '#0f7699' }} className="p-5 sm:p-8 text-center space-y-3 sm:space-y-4">
               <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white/20 rounded-xl sm:rounded-2xl mx-auto flex items-center justify-center text-white">
                 <RefreshCw size={24} className="sm:hidden" />
                 <RefreshCw size={32} className="hidden sm:block" />
               </div>
               <h2 className="text-lg sm:text-xl font-black text-white">{t("dialogs.changePlan.title")}</h2>
            </div>
            <div className="p-5 sm:p-10 space-y-5 sm:space-y-8">
              <p className="text-slate-500 font-medium leading-relaxed text-center text-sm sm:text-base">
                {t("dialogs.changePlan.message", { planName: selectedPlan?.title })}
              </p>
              {selectedPlan?.isRecommended && (
                <div className="bg-sky-50 p-3 sm:p-4 rounded-xl sm:rounded-2xl border border-sky-100 flex items-start space-x-3 sm:space-x-4">
                   <Info className="text-[#0f7699] shrink-0 mt-0.5" size={16} />
                   <p className="text-[11px] font-bold text-[#0f7699] leading-relaxed">{t("dialogs.changePlan.recommendedMessage")}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                 <button onClick={() => setShowChangeModal(false)} className="h-12 sm:h-14 rounded-xl sm:rounded-2xl bg-slate-100 text-slate-600 font-black uppercase tracking-wider text-[10px] sm:text-xs">{t("buttons.cancel")}</button>
                 <button onClick={confirmChange} style={{ backgroundColor: '#0f7699' }} className="h-12 sm:h-14 rounded-xl sm:rounded-2xl text-white font-black uppercase tracking-wider text-[10px] sm:text-xs shadow-lg shadow-sky-900/20">{t("dialogs.changePlan.confirm")}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Plan Modal */}
      {showScheduleChangeModal && selectedPlan && (() => {
        // Berechne ob Upgrade oder Downgrade und Preisdifferenz
        const currentPrice = subscription?.priceMonthly || 0;
        const newPrice = parseFloat(selectedPlan.priceMonthly.replace('€', '').replace(',', '.'));
        const isUpgrade = newPrice > currentPrice;
        const priceDifference = Math.abs(newPrice - currentPrice).toFixed(2);

        return (
        <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center sm:p-4">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md" onClick={() => setShowScheduleChangeModal(false)} />
          <div className="bg-white w-full sm:max-w-2xl rounded-t-[1.5rem] sm:rounded-[2rem] shadow-2xl relative z-10 overflow-hidden animate-in slide-in-from-bottom sm:zoom-in duration-300">
            <div style={{ backgroundColor: '#0f7699' }} className="p-5 sm:p-8 flex items-center justify-between">
               <div className="flex items-center space-x-3 sm:space-x-4">
                 <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-xl sm:rounded-2xl flex items-center justify-center text-white">
                   <Calendar size={20} className="sm:hidden" />
                   <Calendar size={24} className="hidden sm:block" />
                 </div>
                 <h2 className="text-base sm:text-lg font-black text-white">{t("paketepage.scheduledChange")}</h2>
               </div>
               <button onClick={() => setShowScheduleChangeModal(false)} className="p-2 text-white/50 hover:text-white rounded-xl hover:bg-white/10 transition-colors">
                  <X size={20} className="sm:hidden" />
                  <X size={24} className="hidden sm:block" />
               </button>
            </div>

            <div className="p-4 sm:p-8 space-y-4 sm:space-y-5 max-h-[70vh] sm:max-h-[80vh] overflow-y-auto">
              <div className="space-y-4 sm:space-y-5">
                <div className="flex items-center justify-center space-x-2 sm:space-x-4">
                   <div className="text-center p-3 sm:p-4 bg-slate-50 rounded-xl sm:rounded-2xl border border-slate-100 flex-1 min-w-0">
                      <div className="text-[9px] sm:text-[10px] font-black uppercase text-slate-400 mb-1">{t("paketepage.currentPlan")}</div>
                      <div className="text-sm sm:text-base font-black text-slate-800 truncate">{subscription?.planName || 'Free'}</div>
                   </div>
                   <ArrowRight className="text-slate-300 shrink-0" size={18} />
                   <div className="text-center p-3 sm:p-4 bg-[#0f7699]/5 rounded-xl sm:rounded-2xl border border-[#0f7699]/10 flex-1 min-w-0">
                      <div className="text-[9px] sm:text-[10px] font-black uppercase text-[#0f7699] mb-1">{t("paketepage.newPlan")}</div>
                      <div className="text-sm sm:text-base font-black text-[#0f7699] truncate">{selectedPlan.title}</div>
                   </div>
                </div>

                {/* UPGRADE MODE SELECTION - nur bei Upgrades */}
                {isUpgrade && (
                  <div className="bg-white p-3 sm:p-4 rounded-xl sm:rounded-2xl border-2 border-slate-200 space-y-2 sm:space-y-3">
                    <h4 className="text-slate-800 font-black text-[10px] sm:text-xs uppercase tracking-widest">{t("pakete.chooseUpgradeTime")}</h4>

                    <label className={`flex items-start space-x-2 sm:space-x-3 p-2.5 sm:p-3 rounded-lg sm:rounded-xl border-2 cursor-pointer transition-all ${upgradeMode === 'immediate' ? 'border-[#0f7699] bg-[#0f7699]/5' : 'border-slate-200 hover:border-slate-300'}`}>
                      <input
                        type="radio"
                        name="upgradeMode"
                        value="immediate"
                        checked={upgradeMode === 'immediate'}
                        onChange={(e) => setUpgradeMode(e.target.value)}
                        className="mt-0.5 w-4 h-4 text-[#0f7699]"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-slate-800 text-xs sm:text-sm mb-0.5">{t("pakete.immediateUpgrade")}</div>
                        <div className="text-[10px] sm:text-xs text-slate-600 space-y-0.5">
                          <div>• {t("pakete.payNow")}: <strong>{priceDifference}€</strong></div>
                          <div>• {t("pakete.immediateAccessTo")} <strong>{selectedPlan.downloads} {t("pakete.downloads")}</strong></div>
                          <div>• {t("pakete.fromNextMonth")}: {selectedPlan.priceMonthly} / {t("pakete.month")}</div>
                        </div>
                      </div>
                    </label>

                    <label className={`flex items-start space-x-2 sm:space-x-3 p-2.5 sm:p-3 rounded-lg sm:rounded-xl border-2 cursor-pointer transition-all ${upgradeMode === 'next_period' ? 'border-[#0f7699] bg-[#0f7699]/5' : 'border-slate-200 hover:border-slate-300'}`}>
                      <input
                        type="radio"
                        name="upgradeMode"
                        value="next_period"
                        checked={upgradeMode === 'next_period'}
                        onChange={(e) => setUpgradeMode(e.target.value)}
                        className="mt-0.5 w-4 h-4 text-[#0f7699]"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-slate-800 text-xs sm:text-sm mb-0.5">{t("pakete.upgradeNextPeriod")}</div>
                        <div className="text-[10px] sm:text-xs text-slate-600 space-y-0.5">
                          <div>• {t("pakete.payNow")}: <strong>0€</strong></div>
                          <div>• {t("pakete.upgradeEffectiveFrom")}: <strong>{subscription?.nextBillingDate ? new Date(subscription.nextBillingDate).toLocaleDateString('de-DE') : t("pakete.nextMonth")}</strong></div>
                          <div>• {t("pakete.firstPaymentThen")}: {selectedPlan.priceMonthly} / {t("pakete.month")}</div>
                        </div>
                      </div>
                    </label>
                  </div>
                )}

                {/* DOWNGRADE INFO - nur bei Downgrades */}
                {!isUpgrade && (
                  <div className="bg-sky-50/50 p-3 sm:p-4 rounded-xl sm:rounded-2xl border border-sky-100 space-y-2">
                     <h4 className="flex items-center space-x-2 text-[#0f7699] font-black text-[10px] sm:text-xs uppercase tracking-widest">
                        <Info size={14} />
                        <span>{t("paketepage.How does the subscription change work?")}</span>
                     </h4>
                     <ul className="space-y-1 text-slate-600 text-[10px] sm:text-xs font-medium">
                        <li className="flex items-center space-x-2">• {t("paketepage.Downgrade will be effective at period end")}</li>
                        <li className="flex items-center space-x-2">• {t("paketepage.No immediate payment is required")}</li>
                        <li className="flex items-center space-x-2">• {t("paketepage.From this date, your new")} <strong>{selectedPlan.title}</strong> {t("paketepage.subscription will start automatically")}</li>
                     </ul>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-2 sm:gap-3">
                   <div className="p-3 sm:p-4 bg-white border border-slate-100 rounded-xl sm:rounded-2xl shadow-sm">
                      <div className="text-[9px] sm:text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{t("paketepage.newPrice")}</div>
                      <div className="font-black text-slate-800 text-sm sm:text-base">{selectedPlan.priceMonthly} / {t("pakete.month")}</div>
                   </div>
                   <div className="p-3 sm:p-4 bg-white border border-slate-100 rounded-xl sm:rounded-2xl shadow-sm">
                      <div className="text-[9px] sm:text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{isUpgrade ? t("pakete.payNow") : t("paketepage.newDownloads")}</div>
                      <div className="font-black text-[#0f7699] text-sm sm:text-base">
                        {isUpgrade
                          ? (upgradeMode === 'immediate' ? `${priceDifference}€` : '0,00€')
                          : `${selectedPlan.downloads} Units`
                        }
                      </div>
                   </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center sm:justify-center gap-2 sm:space-x-3 pt-2">
                 <button onClick={() => setShowScheduleChangeModal(false)} disabled={loading} className="h-11 sm:h-12 px-6 sm:px-8 rounded-xl sm:rounded-2xl bg-slate-100 text-slate-600 font-bold uppercase tracking-wider text-[10px] sm:text-xs hover:bg-slate-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed order-2 sm:order-1">{t("buttons.cancel")}</button>
                 <button onClick={confirmScheduledChange} disabled={loading} style={{ backgroundColor: '#0f7699' }} className="h-11 sm:h-12 px-6 sm:px-8 rounded-xl sm:rounded-2xl text-white font-bold uppercase tracking-wider text-[10px] sm:text-xs shadow-lg shadow-sky-900/20 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:scale-100 order-1 sm:order-2">
                   {isUpgrade && upgradeMode === 'immediate'
                     ? t("pakete.confirmPaidSwitch")
                     : isUpgrade && upgradeMode === 'next_period'
                     ? t("pakete.confirmNextPeriodSwitch")
                     : t("paketepage.confirmNextMonthChange")
                   }
                 </button>
              </div>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Cancel Scheduled Change Modal */}
      {showCancelScheduledModal && (
        <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center sm:p-4">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md" onClick={() => setShowCancelScheduledModal(false)} />
          <div className="bg-white w-full sm:max-w-lg rounded-t-[1.5rem] sm:rounded-[2rem] shadow-2xl relative z-10 overflow-hidden animate-in slide-in-from-bottom sm:zoom-in duration-300">
            {/* Header */}
            <div style={{ backgroundColor: '#dc2626' }} className="p-5 sm:p-8 flex items-center justify-between">
               <div className="flex items-center space-x-3 sm:space-x-4">
                 <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-xl sm:rounded-2xl flex items-center justify-center text-white">
                   <RefreshCw size={20} className="sm:hidden" />
                   <RefreshCw size={24} className="hidden sm:block" />
                 </div>
                 <h2 className="text-base sm:text-lg font-black text-white">{t("paketepage.cancelScheduled")}</h2>
               </div>
               <button onClick={() => setShowCancelScheduledModal(false)} className="p-2 text-white/50 hover:text-white rounded-xl hover:bg-white/10 transition-colors">
                  <X size={20} className="sm:hidden" />
                  <X size={24} className="hidden sm:block" />
               </button>
            </div>

            {/* Content */}
            <div className="p-4 sm:p-8 space-y-4 sm:space-y-6 max-h-[70vh] overflow-y-auto">
              {/* Info Box */}
              <div className="bg-red-50/50 p-4 sm:p-5 rounded-xl sm:rounded-2xl border border-red-100">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={18} />
                  <div className="space-y-2 flex-1 min-w-0">
                    <p className="text-slate-700 font-medium text-xs sm:text-sm leading-relaxed">
                      {t("paketepage.Möchten Sie den geplanten Abo-Wechsel wirklich stornieren?")}
                    </p>
                    {subscription?.scheduledChange && (
                      <div className="text-[10px] sm:text-xs text-slate-600 space-y-1">
                        <div>• {t("pakete.scheduledSwitchTo")}: <strong>{subscription.scheduledChange.newPlanName}</strong></div>
                        <div>• {t("pakete.effectiveFrom")}: <strong>{new Date(subscription.scheduledChange.effectiveDate).toLocaleDateString('de-DE')}</strong></div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Box */}
              <div className="bg-slate-50 p-4 sm:p-5 rounded-xl sm:rounded-2xl border border-slate-100">
                <h4 className="text-[10px] sm:text-xs font-black text-slate-600 uppercase tracking-widest mb-2 sm:mb-3">{t("pakete.whatHappensOnCancel")}</h4>
                <ul className="space-y-1.5 sm:space-y-2 text-[10px] sm:text-xs text-slate-600">
                  <li className="flex items-center space-x-2">
                    <div className="w-1.5 h-1.5 bg-slate-400 rounded-full shrink-0"></div>
                    <span>{t("pakete.scheduledChangeReverted")}</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <div className="w-1.5 h-1.5 bg-slate-400 rounded-full shrink-0"></div>
                    <span>{t("pakete.currentSubRemains")} <strong>{subscription?.planName}</strong></span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <div className="w-1.5 h-1.5 bg-slate-400 rounded-full shrink-0"></div>
                    <span>{t("pakete.noEffectOnBookedPayments")}</span>
                  </li>
                </ul>
              </div>

              {/* Buttons */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center sm:justify-center gap-2 sm:space-x-3 pt-2">
                 <button
                   onClick={handleCancelScheduledChange}
                   disabled={loading}
                   style={{ backgroundColor: '#dc2626' }}
                   className="h-11 sm:h-12 px-6 sm:px-8 rounded-xl sm:rounded-2xl text-white font-bold uppercase tracking-wider text-[10px] sm:text-xs shadow-lg shadow-red-900/20 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:scale-100 order-1 sm:order-2"
                 >
                   {loading ? t("pakete.cancelling") : t("pakete.cancelNow")}
                 </button>
                 <button
                   onClick={() => setShowCancelScheduledModal(false)}
                   disabled={loading}
                   className="h-11 sm:h-12 px-6 sm:px-8 rounded-xl sm:rounded-2xl bg-slate-100 text-slate-600 font-bold uppercase tracking-wider text-[10px] sm:text-xs hover:bg-slate-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed order-2 sm:order-1"
                 >
                   {t("pakete.abort")}
                 </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 z-[10000] bg-white/60 backdrop-blur-sm flex items-center justify-center">
          <div className="bg-white p-10 rounded-[2.5rem] shadow-2xl border border-white flex flex-col items-center space-y-4">
             <Loader2 size={48} style={{ color: '#0f7699' }} className="animate-spin" />
             <p className="text-slate-800 font-black uppercase tracking-widest text-xs">{t("pakete.processing")}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Pakete;
